import Seo from "@/components/Seo";

export default function PrivacyPolicy() {
  return (
    <article className="container-kore py-20 max-w-3xl">
      <Seo
        title="Privacy Policy"
        description="How Kore Wellbeing collects, uses and protects your personal information under UK GDPR."
        path="/privacy"
      />
      <span className="eyebrow">Legal</span>
      <h1 className="display-1 mt-4 text-kore-forest">Privacy Policy</h1>
      <p className="mt-4 text-sm text-kore-ink/60">Last updated: February 2026</p>

      <div className="prose prose-lg mt-12 space-y-8 text-kore-ink/85 leading-relaxed font-light">
        <p>
          Kore Wellbeing ("Kore", "we", "us", "our") is committed to protecting your privacy. This policy explains what personal information we collect, why we collect it, how we use it, and the choices you have. It applies to this website (<strong>korewellbeing.co.uk</strong>) and to the services provided at our club in Bowness-on-Windermere, Cumbria.
        </p>

        <div>
          <h2 className="font-display text-3xl text-kore-forest mb-3">1. Who we are</h2>
          <p>
            Kore Wellbeing is the data controller for the personal information we hold about you. You can contact us at <a href="mailto:info@korewellbeing.com" className="text-kore-gold underline">info@korewellbeing.com</a> or by post at Kore Wellbeing, Rowan House, Beresford Road, Bowness-on-Windermere, LA23 2JG.
          </p>
        </div>

        <div>
          <h2 className="font-display text-3xl text-kore-forest mb-3">2. Information we collect</h2>
          <p>We collect the following categories of information:</p>
          <ul className="mt-3 space-y-2 pl-6 list-disc marker:text-kore-gold">
            <li><strong>Contact details</strong> — name, email address, phone number, and any message you send via our enquiry, contact or consultation forms.</li>
            <li><strong>Membership data</strong> — held by our booking provider TeamUp: booking history, attendance, health & lifestyle information you choose to share with your coach.</li>
            <li><strong>Payment data</strong> — processed securely by Stripe. We do not store card details on our servers.</li>
            <li><strong>Newsletter data</strong> — email address (and first name, optionally) if you sign up to our journal.</li>
            <li><strong>Website analytics</strong> — anonymised usage data (pages visited, device type, approximate location) collected via privacy-friendly analytics.</li>
          </ul>
        </div>

        <div>
          <h2 className="font-display text-3xl text-kore-forest mb-3">3. How we use your information</h2>
          <ul className="mt-3 space-y-2 pl-6 list-disc marker:text-kore-gold">
            <li>To respond to enquiries and book consultations.</li>
            <li>To deliver your membership, class bookings, and coaching services.</li>
            <li>To process payments and issue receipts.</li>
            <li>To send you occasional updates about Kore (only if you have opted in).</li>
            <li>To improve our website and services.</li>
            <li>To comply with our legal and regulatory obligations.</li>
          </ul>
        </div>

        <div>
          <h2 className="font-display text-3xl text-kore-forest mb-3">4. Lawful bases</h2>
          <p>
            We process personal data under the UK GDPR on one or more of the following lawful bases: your <em>consent</em> (e.g. newsletter subscription), the performance of a <em>contract</em> with you (e.g. delivering your membership), our <em>legitimate interests</em> (e.g. running and improving the club), and <em>legal obligation</em> (e.g. financial records).
          </p>
        </div>

        <div>
          <h2 className="font-display text-3xl text-kore-forest mb-3">5. Sharing your information</h2>
          <p>We share personal information only with the providers necessary to run the club:</p>
          <ul className="mt-3 space-y-2 pl-6 list-disc marker:text-kore-gold">
            <li><strong>TeamUp</strong> — booking, membership and account management.</li>
            <li><strong>Stripe</strong> — payment processing.</li>
            <li><strong>Mailchimp</strong> — newsletter delivery (only if you have subscribed).</li>
            <li><strong>Google Maps</strong> — embedded map on our contact page.</li>
          </ul>
          <p className="mt-3">We never sell your personal data. Each provider has their own privacy policy governing how they handle your information.</p>
        </div>

        <div>
          <h2 className="font-display text-3xl text-kore-forest mb-3">6. How long we keep your information</h2>
          <p>
            We keep personal information only as long as necessary for the purposes above, or as required by law. Membership records are typically retained for the duration of your membership plus seven years for financial-record purposes. Newsletter subscriptions are kept until you unsubscribe.
          </p>
        </div>

        <div>
          <h2 className="font-display text-3xl text-kore-forest mb-3">7. Your rights</h2>
          <p>Under UK data protection law you have the right to: access your data, correct it, request deletion, restrict or object to processing, and receive a copy in a portable format. To exercise any of these rights, please email <a href="mailto:info@korewellbeing.com" className="text-kore-gold underline">info@korewellbeing.com</a>.</p>
          <p className="mt-3">You may also lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noreferrer" className="text-kore-gold underline">ico.org.uk</a>.</p>
        </div>

        <div>
          <h2 className="font-display text-3xl text-kore-forest mb-3">8. Cookies</h2>
          <p>
            Our website uses a small number of essential cookies to keep it working and to remember your cookie preference. We do not use invasive advertising or third-party tracking cookies. You can control cookies at any time via your browser settings.
          </p>
        </div>

        <div>
          <h2 className="font-display text-3xl text-kore-forest mb-3">9. Changes</h2>
          <p>We may update this policy occasionally. The "last updated" date at the top of the page reflects the most recent revision.</p>
        </div>
      </div>
    </article>
  );
}
